// src/components/common/DangerZoneCard.tsx
//
// DangerZoneCard
// - Reusable “attention” section container for destructive / risky / informative actions.
// - Visual treatment is determined by `priority` (danger | warning | info).
// - Keeps layout consistent across pages: header (icon + title) → description → optional body → actions.
//
// Design rules
// - Border uses the priority color (2px).
// - Light mode applies a subtle tinted background; dark mode keeps it transparent.
// - Actions are right-aligned on desktop and stacked on mobile.

import React, { useMemo } from 'react';
import { Paper, Stack, Typography, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Dangerous, InfoOutlined, WarningAmber } from '@mui/icons-material';

export type DangerPriority = 'danger' | 'warning' | 'info';

export type DangerZoneCardProps = {
  /** Visual severity. Defaults to 'danger'. */
  priority?: DangerPriority;

  /** Header title shown next to the icon. */
  title: React.ReactNode;

  /** Optional supporting text under the title. */
  description?: React.ReactNode;

  /** Right-aligned actions area (buttons etc.). */
  actions?: React.ReactNode;

  /** Optional extra content between description and actions. */
  children?: React.ReactNode;
};

type PriorityMeta = {
  paletteKey: 'error' | 'warning' | 'info';
  Icon: typeof Dangerous;
};

const DangerZoneCard = ({
  priority = 'danger',
  title,
  description,
  actions,
  children,
}: DangerZoneCardProps) => {
  const meta = useMemo<PriorityMeta>(() => {
    switch (priority) {
      case 'warning':
        return { paletteKey: 'warning', Icon: WarningAmber };
      case 'info':
        return { paletteKey: 'info', Icon: InfoOutlined };
      case 'danger':
      default:
        return { paletteKey: 'error', Icon: Dangerous };
    }
  }, [priority]);

  const TitleIcon = meta.Icon;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => {
        const main = theme.palette[meta.paletteKey].main;

        return {
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: main,
          backgroundColor:
            theme.palette.mode === 'light' ? alpha(main, 0.06) : 'transparent',
        };
      }}
    >
      <Stack spacing={1.75}>
        {/* Header: icon + title */}
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <TitleIcon
            sx={(theme) => ({
              color: theme.palette[meta.paletteKey].main,
              flexShrink: 0,
              fontSize: 22,
            })}
          />
          <Typography
            variant="h6"
            fontWeight={900}
            sx={(theme) => ({
              color: theme.palette[meta.paletteKey].main,
              lineHeight: 1.1,
              minWidth: 0,
              wordBreak: 'break-word',
            })}
          >
            {title}
          </Typography>
        </Stack>

        {/* Supporting description */}
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {description}
          </Typography>
        ) : null}

        {/* Optional body */}
        {children ? <Box sx={{ pt: 0.25 }}>{children}</Box> : null}

        {/* Actions: right-aligned on desktop, stacked on mobile */}
        {actions ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="flex-end"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
            sx={{ pt: children || description ? 0.5 : 0 }}
          >
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

export default DangerZoneCard;
